import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@clerk/react';
import { AUTO_RESOLVED_SUGGESTIONS_WINDOW_DAYS } from '@this+that/constants';
import type { AutoResolvedSuggestion } from '@this+that/models';

import { loadAutoResolvedSuggestions } from './loadAutoResolvedSuggestions';
import type { AutoResolvedRowState } from '../../enums/autoResolvedRowState';
import { SuggestionsTab } from '../../enums/suggestionsTab';
import {
  type AutoResolvedSuggestionGroup,
  buildSeenStorageKey,
  groupAutoResolvedByDay,
  hasAutoResolvedTabs,
  newestResolvedAt,
  selectReviewCount,
} from '../../pages/private/DoBox/utils/autoResolvedSuggestions';
import { useGetAutoResolvedSuggestionsService } from '../../services/inboxEntries.service';
import { useAutoResolvedSuggestionsStore } from '../../store/DoBox/useAutoResolvedSuggestionsStore';
import { useCustomToast } from '../useCustomToast';

export interface UseAutoResolvedSuggestionsResult {
  isEnabled: boolean;
  hasTabs: boolean;
  items: AutoResolvedSuggestion[];
  groups: AutoResolvedSuggestionGroup[];
  rowStates: Record<string, AutoResolvedRowState>;
  restoredEntryIds: Set<string>;
  reviewCount: number;
  activeTab: SuggestionsTab;
  selectTab: (tab: SuggestionsTab) => void;
  showUnseenDot: boolean;
  isLoading: boolean;
  nowMs: number;
  windowDays: number;
  refetch: () => Promise<void>;
}

const readSeen = (key: string | null): number => {
  if (!key) return 0;
  try {
    const stored = Number(window.localStorage.getItem(key));
    return Number.isFinite(stored) ? stored : 0;
  } catch {
    return 0;
  }
};

const writeSeen = (key: string, value: number) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // storage unavailable: the dot just shows again next visit
  }
};

export const useAutoResolvedSuggestions = (
  taskGroupId: string | null,
): UseAutoResolvedSuggestionsResult => {
  const { t } = useTranslation();
  const { user } = useUser();
  const userId = user?.id;
  const defaultTaskGroupId = user?.unsafeMetadata?.defaultTaskGroupId;
  const { getAutoResolvedSuggestions } = useGetAutoResolvedSuggestionsService();
  const { errorMessage } = useCustomToast();

  const items = useAutoResolvedSuggestionsStore((s) => s.items);
  const rowStates = useAutoResolvedSuggestionsStore((s) => s.rowStates);
  const restoredEntryIds = useAutoResolvedSuggestionsStore(
    (s) => s.restoredEntryIds,
  );
  const activeTab = useAutoResolvedSuggestionsStore((s) => s.activeTab);
  const isLoading = useAutoResolvedSuggestionsStore((s) => s.isLoading);

  const isEnabled = !!taskGroupId && taskGroupId === defaultTaskGroupId;

  const notifyLoadErrorRef = useRef<() => void>(() => undefined);
  notifyLoadErrorRef.current = () =>
    errorMessage(t('doBox.autoResolved.loadError'));

  const load = useCallback(
    async (groupId: string) => {
      try {
        await loadAutoResolvedSuggestions(getAutoResolvedSuggestions, groupId);
      } catch {
        notifyLoadErrorRef.current();
      }
    },
    [getAutoResolvedSuggestions],
  );

  useEffect(() => {
    useAutoResolvedSuggestionsStore
      .getState()
      .reset(isEnabled ? taskGroupId : null);
    if (isEnabled && taskGroupId) void load(taskGroupId);
  }, [isEnabled, taskGroupId, load]);

  const refetch = useCallback(async () => {
    const currentGroupId =
      useAutoResolvedSuggestionsStore.getState().taskGroupId;
    if (currentGroupId) await load(currentGroupId);
  }, [load]);

  const nowMs = useMemo(() => Date.now(), [items]);
  const groups = useMemo(
    () => groupAutoResolvedByDay(items, nowMs, t),
    [items, nowMs, t],
  );
  const reviewCount = useMemo(
    () => selectReviewCount(items, rowStates),
    [items, rowStates],
  );
  const hasTabs = isEnabled && hasAutoResolvedTabs(items);

  const seenKey =
    userId && taskGroupId ? buildSeenStorageKey(userId, taskGroupId) : null;
  const [seen, setSeen] = useState(0);
  useEffect(() => {
    setSeen(readSeen(seenKey));
  }, [seenKey]);

  const newest = newestResolvedAt(items) ?? 0;
  const showUnseenDot =
    hasTabs && activeTab === SuggestionsTab.PENDING && newest > seen;

  const selectTab = useCallback(
    (tab: SuggestionsTab) => {
      const store = useAutoResolvedSuggestionsStore.getState();
      store.setActiveTab(tab);
      if (tab !== SuggestionsTab.RESOLVED || !seenKey) return;
      const newestNow = newestResolvedAt(store.items);
      if (newestNow === null) return;
      writeSeen(seenKey, newestNow);
      setSeen(newestNow);
    },
    [seenKey],
  );

  return {
    isEnabled,
    hasTabs,
    items,
    groups,
    rowStates,
    restoredEntryIds,
    reviewCount,
    activeTab,
    selectTab,
    showUnseenDot,
    isLoading,
    nowMs,
    windowDays: AUTO_RESOLVED_SUGGESTIONS_WINDOW_DAYS,
    refetch,
  };
};
